// src/utils/recibo.utils.js
// ═══════════════════════════════════════════════════════════════════════════
// 🧾 UTILIDADES DE IMPRESIÓN DE RECIBOS - eFIT POS
// ═══════════════════════════════════════════════════════════════════════════
// Función única compartida por POS.jsx, Ventas.jsx y ReciboVenta.jsx.
// Corrige:
//  - @page fuera de @media print (compatibilidad térmica)
//  - total_linea correcto (considera descuento_linea)
//  - descuento global visible en totales
//  - tipo_ncf junto al NCF
//  - onload antes de print() (evita imprimir antes de cargar logo/contenido)
//  - Soporte 58mm y 80mm vía opciones.anchoMm
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Convierte cualquier valor a número seguro (0 si falsy/NaN).
 * @param {*} n
 * @returns {number}
 */
const safe = (n) => Number(n || 0) || 0;

/**
 * Genera el HTML completo de un recibo de venta para impresoras térmicas.
 *
 * @param {Object}  venta                         - Objeto venta. Campos esperados:
 *   numero_venta, fecha, cliente_nombre, cliente_rnc, usuario_nombre,
 *   ncf, tipo_ncf, ncf_nota_credito, ncf_afectado,
 *   detalles[]  →  { material_nombre, cantidad, precio_unitario,
 *                    total_linea, descuento_linea }
 *   subtotal, itbis, descuento, total,
 *   monto_recibido, cambio, metodo_pago, detalle_pago{},
 *   observaciones, estado.
 *
 * @param {Object}  config                        - Config empresa:
 *   empresa_nombre, empresa_rnc, empresa_direccion, empresa_telefono,
 *   fiscal{ impuesto_label, impuesto_tasa, moneda{ simbolo, codigo } }.
 *
 * @param {Object}  [opciones={}]
 * @param {number}  [opciones.anchoMm=80]          - 58 u 80 (mm de papel térmico)
 * @param {boolean} [opciones.esNotaCredito=false]  - Genera layout nota de crédito B04
 *
 * @returns {string}  HTML completo listo para document.write()
 */
export const generarHTMLRecibo = (venta, config, opciones = {}) => {
  const { anchoMm = 80, esNotaCredito = false } = opciones;
  // Área imprimible real: el papel es anchoMm pero el cabezal térmico
  // tiene ~4mm de margen físico a cada lado (72mm en papel de 80mm).
  const areaMm = anchoMm === 80 ? 72 : anchoMm === 58 ? 50 : anchoMm;

  // ── Helpers de formato ───────────────────────────────────────────────
  const simbolo = config?.fiscal?.moneda?.simbolo || 'RD$';
  const impuestoLabel = config?.fiscal?.impuesto_label || 'ITBIS';
  // Prioridad: pos_itbis_porcentaje (lo que usa el backend para calcular)
  // → fiscal.impuesto_tasa (configuración multi-país) → 18 (default dominicano)
  const impuestoTasa = config?.pos_itbis_porcentaje ?? config?.fiscal?.impuesto_tasa ?? 18;
  const fmt            = (n) => `${simbolo}${safe(n).toFixed(2)}`;
  const fmtFecha       = (d) =>
    new Date(d || Date.now()).toLocaleString('es-DO', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });

  // ── Título del documento ─────────────────────────────────────────────
  const titulo = esNotaCredito
    ? 'NOTA DE CRÉDITO (B04)'
    : `VENTA #${venta.numero_venta || ''}`;

  // ── Sección NCF ──────────────────────────────────────────────────────
  // Muestra tipo_ncf + ncf juntos (ej: "B01 - E310000000001")
  let ncfHTML = '';
  if (venta.ncf) {
    const comp = [venta.tipo_ncf, venta.ncf].filter(Boolean).join(' - ');
    ncfHTML += `<div class="row"><span>Comprobante:</span><span>${comp}</span></div>`;
  }
  if (venta.ncf_nota_credito) {
    ncfHTML += `<div class="row"><span>NCF B04:</span><span>${venta.ncf_nota_credito}</span></div>`;
    ncfHTML += `<div class="row"><span>Afecta a:</span><span>${venta.ncf_afectado || ''}</span></div>`;
  }

  // ── Tabla de productos ───────────────────────────────────────────────
  // Usa det.total_linea (calculado por el backend con descuento incluido)
  const detalles = Array.isArray(venta.detalles) ? venta.detalles : [];
  const productosHTML = detalles.map(det => {
    const totalLinea = safe(det.total_linea);
    const descLinea  = safe(det.descuento_linea);
    return `
      <tr>
        <td colspan="3" style="font-weight:600;padding-top:5px;">${det.material_nombre || det.descripcion || ''}</td>
      </tr>
      <tr>
        <td>@ ${fmt(det.precio_unitario)}</td>
        <td style="text-align:center">${det.cantidad}</td>
        <td style="text-align:right">${fmt(totalLinea)}</td>
      </tr>
      ${descLinea > 0 ? `
      <tr>
        <td colspan="3" style="font-size:10px;padding-left:8px;">  Desc.: -${fmt(descLinea)}</td>
      </tr>` : ''}`;
  }).join('');

  // ── Totales — bloque <div class="totales"> ───────────────────────────
  // Cada línea componente va en un <div> simple; el TOTAL usa class="total"
  // cuyo border-top:2px reemplaza al antiguo divider-bold.
  // Fallback: si el servidor devolvió itbis=0 pero la config indica ITBIS
  // activo, calcularlo desde subtotal para no omitir la línea en el recibo.
  const itbisServidor = safe(venta.itbis);
  const itbisActivo   = config?.pos_itbis_activo !== false; // true si no está explícitamente desactivado
  const itbisValor    = itbisServidor > 0
    ? itbisServidor
    : (itbisActivo ? safe(venta.subtotal) * (safe(impuestoTasa) / 100) : 0);
  const itbisLinea = itbisValor > 0
    ? `<div><span>&nbsp;&nbsp;+ ${impuestoLabel} (${impuestoTasa}%):</span><span>${fmt(itbisValor)}</span></div>`
    : '';
  const descuentoLinea = safe(venta.descuento) > 0
    ? `<div><span>&nbsp;&nbsp;&minus; Descuento:</span><span>-${fmt(venta.descuento)}</span></div>`
    : '';

  // ── Método de pago ───────────────────────────────────────────────────
  let pagoHTML = '';
  if (!esNotaCredito) {
    const metodo      = (venta.metodo_pago || '').toLowerCase();
    const detallePago = venta.detalle_pago || {};
    const refHTML     = venta.transferencia_referencia
      ? `<div class="row"><span>Ref. transf.:</span><span style="font-weight:600">${venta.transferencia_referencia}</span></div>`
      : '';
    // Helper para líneas de una tarjeta
    const tarjetaLineas = (tipo, ultimos4, aprobacion, monto) => {
      const partes = [tipo, ultimos4 ? `****${ultimos4}` : null].filter(Boolean).join(' ');
      return `
        ${partes ? `<div class="row"><span>Tarjeta:</span><span>${partes}</span></div>` : ''}
        ${aprobacion ? `<div class="row"><span>Aprobación:</span><span style="font-weight:600">${aprobacion}</span></div>` : ''}
        ${monto != null ? `<div class="row"><span>Monto tarjeta:</span><span>${fmt(monto)}</span></div>` : ''}`;
    };

    if (metodo === 'efectivo') {
      pagoHTML = `
        <div class="row"><span>Recibido:</span><span>${fmt(venta.monto_recibido)}</span></div>
        <div class="row"><span>Cambio:</span><span>${fmt(venta.cambio)}</span></div>`;
    } else if (metodo === 'tarjeta') {
      const tarjetasArr = venta.detalle_pago?.tarjetas;
      if (Array.isArray(tarjetasArr) && tarjetasArr.length > 0) {
        // Múltiples tarjetas
        pagoHTML = tarjetasArr.map((t, i) =>
          `<div style="margin-top:${i>0?'6px':'0'}"><span style="font-size:10px;font-weight:600">TARJETA ${i+1}</span></div>` +
          tarjetaLineas(t.tipo, t.ultimos4, t.aprobacion, t.monto)
        ).join('');
      } else {
        // Tarjeta única
        pagoHTML = tarjetaLineas(venta.tarjeta_tipo, venta.tarjeta_ultimos4, venta.tarjeta_aprobacion, null);
      }
    } else if (metodo === 'transferencia') {
      pagoHTML = refHTML;
    } else if (metodo === 'mixto') {
      pagoHTML = `
        ${safe(detallePago.efectivo)      > 0 ? `<div class="row"><span>Efectivo:</span><span>${fmt(detallePago.efectivo)}</span></div>` : ''}
        ${safe(detallePago.tarjeta)       > 0 ? `<div class="row"><span>Tarjeta:</span><span>${fmt(detallePago.tarjeta)}</span></div>` : ''}
        ${safe(detallePago.transferencia) > 0 ? `<div class="row"><span>Transferencia:</span><span>${fmt(detallePago.transferencia)}</span></div>` : ''}
        ${refHTML}
        ${safe(venta.cambio)              > 0 ? `<div class="row"><span>Cambio:</span><span>${fmt(venta.cambio)}</span></div>` : ''}`;
    }
  }

  // ── Footer y estado ──────────────────────────────────────────────────
  const footerMsg  = esNotaCredito ? '¡Documento Generado!' : '¡Gracias por su compra!';
  const anuladoHTML = venta.estado === 'anulada'
    ? `<div class="divider"></div><div class="anulado">*** VENTA ANULADA ***</div>`
    : '';
  const obsHTML = venta.observaciones
    ? `<div class="info obs">${venta.observaciones}</div>`
    : '';

  // ════════════════════════════════════════════════════════════════════
  // HTML COMPLETO
  // ════════════════════════════════════════════════════════════════════
  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>${esNotaCredito ? 'Nota de Crédito' : 'Recibo'} - ${venta.numero_venta || ''}</title>
  <style>
    /* ── Tamaño de papel térmico — @page FUERA de @media print ────── */
    /* IMPORTANTE: anidar @page dentro de @media print es incorrecto   */
    /* según la spec CSS y algunos drivers de impresora lo ignoran.    */
    @page { size: ${anchoMm}mm auto; margin: 0; }

    /* ── Estilos de impresión ──────────────────────────────────────── */
    @media print { body { margin: 0; } }

    /* ── Base ──────────────────────────────────────────────────────── */
    * { box-sizing: border-box; }
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 12px;
      line-height: 1.4;
      width: ${areaMm}mm;
      max-width: ${areaMm}mm;
      margin: 0 auto;
      padding: 8px;
      color: #000;
      background: #fff;
    }

    /* ── Encabezado ────────────────────────────────────────────────── */
    .header     { text-align: center; margin-bottom: 8px; }
    .emp-nombre { font-size: 16px; font-weight: bold; margin: 4px 0; text-transform: uppercase; }
    .info       { font-size: 11px; margin: 2px 0; }
    .obs        { font-size: 10px; font-style: italic; margin-top: 6px; }

    /* ── Separador ─────────────────────────────────────────────────── */
    .divider { border: none; border-top: 1px dashed #000; margin: 8px 0; }

    /* ── Número de venta y filas clave:valor ───────────────────────── */
    .titulo { text-align: center; font-size: 14px; font-weight: bold; margin: 5px 0; }
    .row    { display: flex; justify-content: space-between; margin: 3px 0; font-size: 11px; }

    /* ── Tabla de productos ─────────────────────────────────────────── */
    /* Anchos fijos evitan que nombres largos colapsen columnas numéricas */
    table  { width: 100%; border-collapse: collapse; margin: 6px 0; }
    col:nth-child(1) { width: 46%; }   /* Producto  */
    col:nth-child(2) { width: 12%; }   /* Cant.     */
    col:nth-child(3) { width: 42%; }   /* Total     */
    th { font-size: 10px; font-weight: bold; border-bottom: 1px solid #000; padding: 4px 0; }
    th:nth-child(1) { text-align: left;   }
    th:nth-child(2) { text-align: center; }
    th:nth-child(3) { text-align: right;  }
    td { font-size: 11px; padding: 2px 0; word-break: break-word; }

    /* ── Totales ────────────────────────────────────────────────────── */
    /* Cada línea del desglose (Subtotal, ITBIS, Descuento) es un flex row  */
    /* La línea .total tiene border-top 2px que actúa de separador grueso  */
    .totales        { margin: 4px 0; }
    .totales > div  { display: flex; justify-content: space-between; font-size: 11px; margin: 3px 0; }
    .totales .total { display: flex; justify-content: space-between; font-size: 14px; font-weight: bold; margin-top: 5px; padding-top: 5px; border-top: 2px solid #000; }

    /* ── Footer ─────────────────────────────────────────────────────── */
    .footer      { text-align: center; margin-top: 10px; }
    .footer-text { font-size: 12px; font-weight: bold; }
    .anulado     { text-align: center; font-size: 14px; font-weight: bold; text-transform: uppercase; margin: 5px 0; }
  </style>
</head>
<body>

  <!-- ── ENCABEZADO EMPRESA ─────────────────────────────────────────── -->
  <div class="header">
    <div class="emp-nombre">${config?.empresa_nombre || 'MI EMPRESA'}</div>
    ${config?.empresa_rnc       ? `<div class="info">RNC: ${config.empresa_rnc}</div>` : ''}
    ${config?.empresa_direccion ? `<div class="info">${config.empresa_direccion}</div>` : ''}
    ${config?.empresa_telefono  ? `<div class="info">Tel: ${config.empresa_telefono}</div>` : ''}
  </div>

  <div class="divider"></div>

  <!-- ── DATOS GENERALES ────────────────────────────────────────────── -->
  <div class="titulo">${titulo}</div>
  <div class="row"><span>Fecha:</span><span>${fmtFecha(venta.fecha)}</span></div>
  <div class="row"><span>Cliente:</span><span>${venta.cliente_nombre || 'Cliente General'}</span></div>
  ${venta.cliente_rnc ? `<div class="row"><span>RNC/Cédula:</span><span>${venta.cliente_rnc}</span></div>` : ''}
  <div class="row"><span>Cajero:</span><span>${venta.usuario_nombre || ''}</span></div>
  ${ncfHTML}

  <div class="divider"></div>

  <!-- ── TABLA DE PRODUCTOS ─────────────────────────────────────────── -->
  <table>
    <colgroup><col><col><col></colgroup>
    <thead>
      <tr>
        <th>Producto</th>
        <th style="text-align:center">Cant.</th>
        <th style="text-align:right">Total</th>
      </tr>
    </thead>
    <tbody>${productosHTML}</tbody>
  </table>

  <div class="divider"></div>

  <!-- ── TOTALES ────────────────────────────────────────────────────── -->
  <div class="totales">
    <div><span>Subtotal:</span><span>${fmt(venta.subtotal)}</span></div>
    ${itbisLinea}
    ${descuentoLinea}
    <div class="total"><span>TOTAL:</span><span>${fmt(venta.total)}</span></div>
  </div>

  <!-- ── MÉTODO DE PAGO ─────────────────────────────────────────────── -->
  ${!esNotaCredito ? `
  <div class="divider"></div>
  <div class="row"><span>Método:</span><span>${(venta.metodo_pago || '').toUpperCase()}</span></div>
  ${pagoHTML}` : ''}

  ${anuladoHTML}
  ${obsHTML}

  <!-- ── PIE DE PÁGINA ──────────────────────────────────────────────── -->
  <div class="footer">
    <div class="divider"></div>
    <div class="footer-text">${footerMsg}</div>
    <div class="info" style="margin-top:4px;">${new Date(venta.fecha || Date.now()).toLocaleDateString('es-DO')}</div>
    <div class="info">Este documento es un comprobante de compra</div>
  </div>

</body>
</html>`;
};

/**
 * Abre una ventana emergente, escribe el HTML y dispara la impresión
 * DESPUÉS de que todo el contenido (incluido el logo si lo hay) cargue.
 * Usa window.onload en lugar de setTimeout para evitar imprimir antes
 * de que las imágenes o estilos terminen de cargar.
 *
 * @param {string} html - HTML generado por generarHTMLRecibo()
 */
export const abrirVentanaImpresion = (html) => {
  // width=400 garantiza margen suficiente para 80mm (≈302px a 96dpi)
  const ventana = window.open('', '_blank', 'width=400,height=650');
  if (!ventana) {
    console.warn('⚠️ Ventana de impresión bloqueada. Permite pop-ups para este sitio.');
    return;
  }
  ventana.document.write(html);
  ventana.document.close();
  // onload espera a que imágenes y estilos carguen antes de imprimir
  ventana.onload = () => {
    ventana.print();
    ventana.close();
  };
};
